// `client-contact-async-operations.api.ng1.ts` — the queue behind a contact's long-running
// operations: "Get phone and email" (GET_LATEST_DETAILS) and "Create in ATS" / "Update existing
// contact" (CREATE_ / UPDATE_CLIENT_CONTACT_IN_ATS).
//
// All three are POSTs with a JSON body and all three are reads: the pending lists come from the
// operation table, and `getCompletedOperationResults` looks up the results of the message ids it
// is given (`ClientContactAsyncOperationController`). The WRITES that enqueue an operation live
// with their features (`client-contact-create.ts`).

import { request } from './request.ts'

export const ClientContactAsyncOperationType = {
  GET_LATEST_DETAILS: 'GET_LATEST_DETAILS',
  CREATE_CLIENT_CONTACT_IN_ATS: 'CREATE_CLIENT_CONTACT_IN_ATS',
  UPDATE_CLIENT_CONTACT_IN_ATS: 'UPDATE_CLIENT_CONTACT_IN_ATS',
} as const
export type ClientContactAsyncOperationType =
  (typeof ClientContactAsyncOperationType)[keyof typeof ClientContactAsyncOperationType]

/** `IClientContactGetPendingOperationDto`. `id` is the message id the enqueueing call answered. */
export interface ClientContactPendingOperation {
  id: string
  clientContactId: number
  operationType: ClientContactAsyncOperationType
}

export interface ClientContactPendingOperationsResponse {
  operations?: ClientContactPendingOperation[] | null
}

/**
 * `IClientContactGetCompletedOperationDto`. `result` is per type — the contact-details DTO for
 * GET_LATEST_DETAILS, `{ successful, result }` for the two ATS writes — and `null` when `failed`.
 */
export interface ClientContactCompletedOperation {
  id: string
  clientContactId: number
  operationType: ClientContactAsyncOperationType
  result: unknown
  /** True when the operation ended in a terminal FAILED state; `result` is then null. */
  failed?: boolean
}

export interface ClientContactCompletedOperationsResponse {
  results?: ClientContactCompletedOperation[] | null
}

/** `IClientContactAsyncMessageResponse` — what an enqueueing write answers. */
export interface ClientContactAsyncMessageResponse {
  successful: boolean
  messageId: string
}

export async function getClientContactPendingOperationsByAlertId(params: {
  alertId: number
}): Promise<ClientContactPendingOperationsResponse | null> {
  return request('POST', '/chrome-extension-api/client-contact/async-operation/getPendingOperationsByAlertId', params)
}

export async function getClientContactPendingOperationsByClientContactIds(params: {
  clientContactIds: number[]
}): Promise<ClientContactPendingOperationsResponse | null> {
  return request(
    'POST',
    '/chrome-extension-api/client-contact/async-operation/getPendingOperationsByClientContactIds',
    params,
  )
}

/**
 * Polled every two seconds while anything is pending (`ClientContactAsyncContainer`). Upstream
 * marks it `ignoreLoadingBar`, which only hides the loading bar: a failure still reaches the http
 * interceptor's alert, and the poll's caller swallows the rejection and tries again next tick.
 */
export async function getClientContactCompletedOperationResults(params: {
  messageIds: string[]
}): Promise<ClientContactCompletedOperationsResponse | null> {
  return request(
    'POST',
    '/chrome-extension-api/client-contact/async-operation/getCompletedOperationResults',
    params,
  )
}
